// // ─────────────────────────────────────────────────────────────────────────────
// // FILE: src/components/HRComponents/CandidateDetail.jsx
// // ─────────────────────────────────────────────────────────────────────────────
// import React, { useState, useEffect, useCallback, useMemo } from 'react';
// import { useParams } from 'react-router-dom';
// import { FaArrowLeft } from 'react-icons/fa';
// import { 
//   Mail, Phone, MapPin, Calendar, Award, Clock, ChevronRight, 
//   FileText, Plus, ShieldCheck, Star, Tags, AlertCircle, User, Info, Edit 
// } from 'lucide-react';
// import { toast } from 'react-toastify';
// import PageLoader from '../../helpers/PageLoader';
// import SummaryApi from '../../common/SummaryApi';
// import api from '../../common/apiClient';
// import CandidateForm from './CandidateForm';
// import FormOffCanvas from '../ui/FormOffCanvas';
// import MoveStageDrawer from './MoveStageDrawer';
// import SelectDropdown from '../../customStyles/SelectDropdown'; // 👈 Imported custom SelectDropdown
// import { formatDateTime } from '../../helpers/formatDateTime';

// // Static configurations for SelectDropdown options
// const modeOptions = [
//   { value: 'online', label: 'Online' },
//   { value: 'in_person', label: 'In Person' },
//   { value: 'phone', label: 'Phone' }
// ];

// const ratingOptions = [
//   { value: 1, label: '1 - Very Poor' },
//   { value: 2, label: '2 - Below Average' },
//   { value: 3, label: '3 - Good' },
//   { value: 4, label: '4 - Strong Candidate' },
//   { value: 5, label: '5 - Excellent Fit' }
// ];

// const recommendationOptions = [
//   { value: 'strong_yes', label: 'Strong Yes' },
//   { value: 'yes', label: 'Yes / Hire' },
//   { value: 'neutral', label: 'Neutral' },
//   { value: 'no', label: 'No / Reject' },
//   { value: 'strong_no', label: 'Strong No' }
// ];

// const CandidateDetail = ({ id: propId, onClose }) => { 
//   const { id: urlId } = useParams();
//   const id = propId || urlId; 

//   const [candidate, setCandidate] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [activeTab, setActiveTab] = useState('pipeline'); // 'pipeline' | 'interviews' | 'job_specs'

//   // Dynamic Employee options state
//   const [users, setUsers] = useState([]);

//   // Stage transition Offcanvas State
//   const [isMoveStageOpen, setIsMoveStageOpen] = useState(false);
//   const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);

//   // Default date and time calculations to populate current system timings [1, 2]
//   const todayDateString = new Date().toISOString().split('T')[0];
//   const now = new Date();
//   const currentTimeString = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

//   // Separate Date & Time input states [2]
//   const [interviewDate, setInterviewDate] = useState(todayDateString);
//   const [interviewTime, setInterviewTime] = useState(currentTimeString);

//   // Schedule/Edit Interview Offcanvas State
//   const [isScheduleOpen, setIsScheduleOpen] = useState(false);
//   const [editingInterviewId, setEditingInterviewId] = useState(null); // 👈 Track if we are editing an existing interview
//   const [interviewForm, setInterviewForm] = useState({
//     round: 1,
//     roundName: '',
//     durationMinutes: 30,
//     mode: 'online',
//     meetingLink: '',
//     location: '',
//     interviewers: [] // Dynamic array of selected User IDs [3]
//   });
//   const [isScheduling, setIsScheduling] = useState(false);

//   // Submit Feedback Offcanvas State
//   const [selectedInterviewForFeedback, setSelectedInterviewForFeedback] = useState(null);
//   const [feedbackForm, setFeedbackForm] = useState({
//     rating: 3,
//     comments: '',
//     recommendation: 'yes'
//   });
//   const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

//   // Fetch the candidate database record
//   const fetchCandidateRecord = useCallback(async () => {
//     setLoading(true);
//     try {
//       const response = await api.get(SummaryApi.getCandidatesById.url(id));
//       if (response.data.success) {
//         setCandidate(response.data.candidate);
//       }
//     } catch (err) {
//       toast.error('Failed to resolve candidate metrics');
//     } finally {
//       setLoading(false);
//     }
//   }, [id]);

//   // Fetch all employees/users from the system to populate interviewers [1]
//   const fetchUsers = useCallback(async () => {
//     try {
//       const response = await api.get(SummaryApi.getAllUsers.url);
//       if (response.data.success) {
//         setUsers(response.data.data || []);
//       }
//     } catch (err) {
//       console.error('Failed to load employees list:', err);
//     }
//   }, []);

//   useEffect(() => {
//     fetchCandidateRecord();
//     fetchUsers(); // Trigger dynamic employee list load [1]
//   }, [fetchCandidateRecord, fetchUsers]);

//   // Merge dynamic properties of users for clear dropdown labelling
//   const userOptions = useMemo(() => {
//     return users.map(u => ({
//       ...u,
//       fullName: `${u.firstName} ${u.lastName} (${u.email})`
//     }));
//   }, [users]);

//   // Handle Edit Click on an interview round
//   const handleEditClick = (round) => {
//     setEditingInterviewId(round._id);
//     setInterviewForm({
//       round: round.round,
//       roundName: round.roundName || '',
//       durationMinutes: round.durationMinutes || 30,
//       mode: round.mode || 'online',
//       meetingLink: round.meetingLink || '',
//       location: round.location || '',
//       interviewers: round.interviewers?.map((i) => i._id) || []
//     });

//     if (round.scheduledAt) {
//       const d = new Date(round.scheduledAt);
//       const year = d.getFullYear();
//       const month = String(d.getMonth() + 1).padStart(2, '0');
//       const day = String(d.getDate()).padStart(2, '0');
//       const dateStr = `${year}-${month}-${day}`;
//       const timeStr = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      
//       setInterviewDate(dateStr);
//       setInterviewTime(timeStr);
//     }
//     setIsScheduleOpen(true);
//   };

//   // Close Schedule/Edit Modal and reset states
//   const handleCloseSchedule = () => {
//     setIsScheduleOpen(false);
//     setEditingInterviewId(null);
//     setInterviewForm({
//       round: 1,
//       roundName: '',
//       durationMinutes: 30,
//       mode: 'online',
//       meetingLink: '',
//       location: '',
//       interviewers: []
//     });
//     setInterviewDate(todayDateString);
//     setInterviewTime(currentTimeString);
//   };

//   // Combine separate date and time input values to build a single ISO string payload [2]
//   const handleScheduleSubmit = async (e) => {
//     e.preventDefault();
//     if (!interviewDate || !interviewTime) {
//       toast.warning('Please specify both Date and Time');
//       return;
//     }
//     if (interviewForm.interviewers?.length === 0) {
//       toast.warning('Please select at least one interviewer');
//       return;
//     }

//     setIsScheduling(true);

//     try {
//       // Create combined ISO timestamp atomically [2]
//       const scheduledAtDateTime = new Date(`${interviewDate}T${interviewTime}`).toISOString();

//       const payload = {
//         round: Number(interviewForm.round),
//         roundName: interviewForm.roundName,
//         scheduledAt: scheduledAtDateTime, // Unified Date-Time ISO payload [2]
//         durationMinutes: Number(interviewForm.durationMinutes),
//         mode: interviewForm.mode,
//         meetingLink: interviewForm.meetingLink,
//         location: interviewForm.location,
//         interviewers: interviewForm.interviewers // Directly parsed array of User ObjectIds [3]
//       };

//       let response;
//       if (editingInterviewId) {
//         // 👈 Edit mode: Trigger PUT update API
//         response = await api.put(
//           SummaryApi.candidateScheduleInterviewUpdate.url(id, editingInterviewId), 
//           payload
//         );
//       } else {
//         // Create mode: Trigger POST schedule API
//         response = await api.post(
//           SummaryApi.candidateScheduleInterview.url(id), 
//           payload
//         );
//       }

//       if (response.data.success) {
//         toast.success(editingInterviewId ? 'Interview rescheduled successfully' : 'Interview scheduled successfully');
//         handleCloseSchedule();
//         fetchCandidateRecord();
//       }
//     } catch (err) {
//       toast.error(editingInterviewId ? 'Could not update interview schedule' : 'Could not schedule interview slot');
//     } finally {
//       setIsScheduling(false);
//     }
//   };

//   const handleFeedbackSubmit = async (e) => {
//     e.preventDefault();
//     setIsSubmittingFeedback(true);
//     try {
//       const response = await api.post(
//         SummaryApi.candidateSubmitInterviewFeedback.url(id, selectedInterviewForFeedback._id), 
//         feedbackForm
//       );
//       if (response.data.success) {
//         toast.success('Feedback successfully registered');
//         setSelectedInterviewForFeedback(null);
//         setFeedbackForm({ rating: 3, comments: '', recommendation: 'yes' });
//         fetchCandidateRecord();
//       }
//     } catch (err) {
//       toast.error('Feedback record failure');
//     } finally {
//       setIsSubmittingFeedback(false);
//     }
//   };

//   // Helper function to render star ratings dynamically
//   const renderStars = (ratingValue) => {
//     return (
//       <div className="flex items-center gap-0.5">
//         {[1, 2, 3, 4, 5].map((star) => (
//           <Star 
//             key={star} 
//             className={`h-4.5 w-4.5 ${star <= (ratingValue || 0) ? 'text-amber-500 fill-amber-500' : 'text-gray-200'}`} 
//           />
//         ))}
//       </div>
//     );
//   };

//   if (loading || !candidate) return <PageLoader />;

//   return (
//     <>
//     <div className="space-y-6">
      
//       {/* Hold State Alert Banner */}
//       {candidate.isOnHold && (
//         <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-xl flex items-center gap-3 shadow-sm animate-pulse">
//           <AlertCircle className="h-5 w-5 text-amber-500 shrink-0" />
//           <div className="text-xs text-amber-800 font-semibold">
//             This candidate is currently marked on <span className="font-extrabold uppercase">Hold</span>. Internal evaluations are paused.
//           </div>
//         </div>
//       )}

//       {/* Header Panel (Fully Responsive Flex Layout) */}
//       <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 bg-white p-4 md:p-6 rounded-xl border border-gray-100 shadow-sm">
//         <div className="flex items-center gap-3">
//           <button 
//             onClick={onClose} 
//             type="button"
//             className="rounded-lg bg-red-50 border border-red-100 p-2.5 text-textPrimary hover:text-primary transition-colors shrink-0"
//           >
//             <FaArrowLeft className="w-4 h-4" />
//           </button>
//           <div>
//             <div className="flex flex-wrap items-center gap-2">
//               <h1 className="text-lg sm:text-xl font-bold text-gray-900 leading-tight capitalize">{candidate.firstName} {candidate.lastName}</h1>
//               <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border bg-red-50 text-primary border-red-100">
//                 {candidate.applicationId}
//               </span>
//             </div>
//             <p className="text-xs text-gray-400 mt-1">Applied For: <span className="font-semibold text-gray-700">{candidate.jobOpeningId?.title}</span></p>
//           </div>
//         </div>
        
//         {/* Responsive Action Buttons Grid */}
//         <div className="grid grid-cols-3 gap-2 w-full lg:w-auto shrink-0">
//           <button 
//             onClick={() => setIsEditProfileOpen(true)} 
//             className="flex items-center justify-center gap-1.5 border border-gray-300 hover:bg-gray-100 text-gray-700 px-3 py-2 rounded-lg text-xs font-bold transition-colors"
//           >
//             Edit Profile
//           </button>
//           <button 
//             onClick={() => setIsScheduleOpen(true)} 
//             className="flex items-center justify-center gap-1.5 border border-gray-300 hover:bg-gray-100 text-gray-700 px-3 py-2 rounded-lg text-xs font-bold"
//           >
//             <Calendar className="h-3.5 w-3.5" /> Schedule
//           </button>
//           <button 
//             onClick={() => { setIsMoveStageOpen(true); }} 
//             className="flex items-center justify-center gap-1.5 bg-primary hover:bg-secondary text-white px-3 py-2 rounded-lg text-xs font-bold transition-colors"
//           >
//             Move Stage
//           </button>
//         </div>
//       </div>

//       {/* Main Responsive Grid Layout */}
//       <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
//         {/* Left Sidebar Panel (Profile Overview + Financial Metrics) */}
//         <div className="lg:col-span-1 space-y-6">
//           <div className="bg-white border rounded-xl p-5 shadow-sm space-y-5">
//             <div className="flex justify-between items-center border-b pb-3">
//               <h2 className="font-bold text-gray-900 text-sm uppercase tracking-wider">Candidate File</h2>
//               {renderStars(candidate.rating)}
//             </div>
            
//             <div className="space-y-4">
//               <div className="flex items-center gap-3 text-xs text-gray-600">
//                 <Mail className="h-4 w-4 text-gray-400 shrink-0" /> <span>{candidate.email}</span>
//               </div>
//               <div className="flex items-center gap-3 text-xs text-gray-600">
//                 <Phone className="h-4 w-4 text-gray-400 shrink-0" /> <span>{candidate.mobile || 'Not provided'}</span>
//               </div>
//               <div className="flex items-center gap-3 text-xs text-gray-600">
//                 <MapPin className="h-4 w-4 text-gray-400 shrink-0" /> <span>{candidate.currentLocation || 'N/A'}</span>
//               </div>
//               <div className="flex items-center gap-3 text-xs text-gray-600">
//                 <Award className="h-4 w-4 text-gray-400 shrink-0" /> <span>{candidate.totalExperienceYears} Years Total Experience</span>
//               </div>
//             </div>

//             {/* Candidate Sourcing Details */}
//             <div className="border-t pt-4 space-y-2">
//               <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Application Source</span>
//               <span className="capitalize text-xs font-semibold text-gray-700 bg-gray-50 border px-3 py-1.5 rounded-lg inline-block">
//                 {candidate.source || 'Website'}
//               </span>
//             </div>

//             {/* Core Skills Badges */}
//             <div className="border-t pt-4 space-y-2">
//               <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Skills & Expertise</span>
//               {candidate.skills?.length > 0 ? (
//                 <div className="flex flex-wrap gap-1">
//                   {candidate.skills.map((skill, idx) => (
//                     <span key={idx} className="bg-red-50 text-primary border border-red-100 px-2.5 py-0.5 rounded text-[10px] font-semibold">{skill}</span>
//                   ))}
//                 </div>
//               ) : (
//                 <p className="text-xs text-gray-400 italic">No skills listed</p>
//               )}
//             </div>

//             {/* Recruiter Custom Tags */}
//             {candidate.tags?.length > 0 && (
//               <div className="border-t pt-4 space-y-2">
//                 <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1">
//                   <Tags className="h-3.5 w-3.5 text-gray-400" /> Recruiter Tags
//                 </span>
//                 <div className="flex flex-wrap gap-1">
//                   {candidate.tags.map((tag, idx) => (
//                     <span key={idx} className="bg-gray-100 text-gray-700 border border-gray-200 px-2 py-0.5 rounded text-[10px] font-semibold">#{tag}</span>
//                   ))}
//                 </div>
//               </div>
//             )}

//             {/* Resume Upload Attachments */}
//             <div className="border-t pt-4 space-y-3">
//               <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Submitted Credentials</h3>
//               {candidate.documents?.length > 0 ? (
//                 candidate.documents.map((doc, idx) => (
//                   <a key={idx} href={doc.fileUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 p-2.5 bg-gray-50 hover:bg-red-50/50 text-gray-700 rounded-lg border text-xs transition-all">
//                     <FileText className="h-4.5 w-4.5 text-red-500 shrink-0" />
//                     <span className="truncate flex-1 font-semibold">{doc.fileName || 'Resume Document'}</span>
//                   </a>
//                 ))
//               ) : (
//                 <p className="text-xs text-gray-400 italic">No credentials submitted</p>
//               )}
//             </div>
//           </div>

//           {/* Financial Profile Card with Indian Rupees Symbol formatting */}
//           <div className="bg-white border rounded-xl p-5 shadow-sm space-y-4">
//             <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider border-b pb-2">Financial Profile Config</h3>
//             <div className="space-y-4">
//               <div>
//                 <span className="text-[10px] font-bold text-gray-400 uppercase block">Current CTC</span>
//                 <p className="text-sm font-extrabold text-gray-800 mt-1 flex items-center gap-1">
//                   {candidate.currentCtc ? `₹ ${candidate.currentCtc.toLocaleString('en-IN')} / year` : 'N/A'}
//                 </p>
//               </div>
//               <div>
//                 <span className="text-[10px] font-bold text-gray-400 uppercase block">Expected CTC</span>
//                 <p className="text-sm font-extrabold text-gray-800 mt-1 flex items-center gap-1">
//                   {candidate.expectedCtc ? `₹ ${candidate.expectedCtc.toLocaleString('en-IN')} / year` : 'N/A'}
//                 </p>
//               </div>
//               <div>
//                 <span className="text-[10px] font-bold text-gray-400 uppercase block">Notice Period Duration</span>
//                 <p className="text-xs font-semibold text-gray-700 mt-1 bg-gray-50 border p-2.5 rounded-lg inline-block">
//                   {candidate.noticePeriodDays ? `${candidate.noticePeriodDays} Days Notice` : 'Immediate Joiner'}
//                 </p>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* Right Section Panel (Tabs Workspace with highly detailed content) */}
//         <div className="lg:col-span-2 space-y-4">
          
//           {/* Workspace Tabs Navigation Menu */}
//           <div className="flex border-b border-gray-200 overflow-x-auto custom-scrollbar">
//             <button onClick={() => setActiveTab('pipeline')} className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors whitespace-nowrap ${activeTab === 'pipeline' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
//               Pipeline Tracker
//             </button>
//             <button onClick={() => setActiveTab('interviews')} className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors whitespace-nowrap ${activeTab === 'interviews' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
//               Interview Cycles ({candidate.interviews?.length || 0})
//             </button>
//             <button onClick={() => setActiveTab('job_specs')} className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors whitespace-nowrap ${activeTab === 'job_specs' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
//               Job Posting Specs
//             </button>
//           </div>

//           <div className="bg-white border rounded-xl p-6 shadow-sm min-h-[400px]">
            
//             {/* TAB 1: PIPELINE / WORKFLOW LOGS TIMELINE */}
//             {activeTab === 'pipeline' && (
//               <div className="space-y-6 animate-in fade-in duration-300">
//                 <h3 className="font-bold text-gray-900 text-sm uppercase tracking-wider">Recruitment Trail Log</h3>
//                 {candidate.stageHistory?.length > 0 ? (
//                   <div className="relative border-l border-gray-200 pl-6 ml-2 space-y-6">
//                     {candidate.stageHistory.map((history, idx) => (
//                       <div key={idx} className="relative">
//                         <span className="absolute -left-[31px] mt-1 bg-red-100 text-primary border-4 border-white h-5 w-5 rounded-full flex items-center justify-center shadow-sm">
//                           <Clock className="h-2.5 w-2.5" />
//                         </span>
//                         <div>
//                           <div className="flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
//                             <span className="capitalize font-semibold text-gray-800 bg-gray-100 px-2 py-0.5 rounded">{history.fromStage || 'Applied'}</span>
//                             <ChevronRight className="h-3 w-3 text-gray-400" />
//                             <span className="capitalize font-bold text-red-700 bg-red-50 px-2 py-0.5 rounded">{history.toStage}</span>
//                           </div>
//                           {history.remarks && <p className="text-xs text-gray-600 mt-1.5 font-medium italic">"{history.remarks}"</p>}
                          
//                           {/* Displaying Changer's details explicitly if available */}
//                           <div className="text-[10px] text-gray-400 mt-1.5 flex flex-wrap gap-x-2 gap-y-1 items-center">
//                             <User className="h-3 w-3" />
//                             <span className="font-semibold text-gray-500">
//                               Changer: {typeof history.changedBy === 'object' ? `${history.changedBy.firstName} ${history.changedBy.lastName} (${history.changedBy.email})` : 'System / Auto-Update'}
//                             </span>
//                             <span>&bull;</span>
//                             <span>{formatDateTime(history.changedAt, false)}</span>
//                           </div>
//                         </div>
//                       </div>
//                     ))}
//                   </div>
//                 ) : (
//                   <p className="text-xs text-gray-400 italic">No transition trails logged yet.</p>
//                 )}
//               </div>
//             )}

//             {/* TAB 2: INTERVIEW HISTORY (DETAILED) */}
//             {activeTab === 'interviews' && (
//               <div className="space-y-6 animate-in fade-in duration-300">
//                 <h3 className="font-bold text-gray-900 text-sm uppercase tracking-wider">Evaluation Cycles</h3>
//                 {candidate.interviews?.length > 0 ? (
//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                     {candidate.interviews.map((round) => (
//                       <div key={round._id} className="border rounded-xl p-4 space-y-3 bg-gray-50/50 shadow-sm">
//                         <div className="flex justify-between items-start border-b pb-2">
//                           <div>
//                             <span className="text-[9px] uppercase tracking-wider text-primary font-bold">ROUND {round.round}</span>
//                             <h4 className="font-bold text-gray-800 text-xs">{round.roundName || 'Technical Round'}</h4>
//                           </div>
//                           <div className="flex items-center gap-2 shrink-0">
//                             {/* 
//                               👉 Edit Schedule Button:
//                               Only rendered if no feedback is submitted yet.
//                             */}
//                             {(!round.feedback || round.feedback.length === 0) && (
//                               <button 
//                                 onClick={() => handleEditClick(round)}
//                                 type="button"
//                                 className="p-1 hover:bg-gray-100 text-gray-500 hover:text-primary rounded transition-colors"
//                                 title="Edit Schedule"
//                               >
//                                 <Edit className="h-3.5 w-3.5" />
//                               </button>
//                             )}
//                             <span className={`px-2 py-0.5 text-[9px] font-bold rounded capitalize border ${round.status === 'completed' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
//                               {round.status}
//                             </span>
//                           </div>
//                         </div>

//                         <div className="text-xs text-gray-500 space-y-1.5">
//                           <div className="flex items-center gap-2">
//                             <Clock className="h-3.5 w-3.5 text-gray-400 shrink-0" /> 
//                             <span>{formatDateTime(round.scheduledAt, false)} ({round.durationMinutes} min)</span>
//                           </div>
//                           <div className="flex items-center gap-2">
//                             <User className="h-3.5 w-3.5 text-gray-400 shrink-0" />
//                             <span>Scheduled By: {round.scheduledBy ? `${round.scheduledBy.firstName} ${round.scheduledBy.lastName}` : 'System'}</span>
//                           </div>
//                           {round.meetingLink && (
//                             <div className="flex items-center gap-2">
//                               <ShieldCheck className="h-3.5 w-3.5 text-green-600 shrink-0" /> 
//                               <a href={round.meetingLink} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline font-medium">Join Google Meet</a>
//                             </div>
//                           )}
//                         </div>

//                         {/* Displaying scheduled Interviewers list with complete name details */}
//                         <div className="space-y-1.5 pt-1 border-t">
//                           <span className="text-[9px] font-bold text-gray-400 uppercase block">Interviewers Panel</span>
//                           <div className="flex flex-wrap gap-1">
//                             {round.interviewers?.map((interviewer) => (
//                               <span key={interviewer._id} className="inline-block bg-white text-gray-700 border border-gray-200 rounded px-2 py-0.5 text-[10px] font-semibold shadow-xs">
//                                 {interviewer.firstName} {interviewer.lastName} ({interviewer.email})
//                               </span>
//                             ))}
//                           </div>
//                         </div>

//                         {/* Interview Feedback Log with detailed names */}
//                         <div className="border-t pt-2 space-y-2">
//                           <div className="flex justify-between items-center">
//                             <span className="text-[9px] font-bold text-gray-400 uppercase">Interviewer Evaluations</span>
                            
//                             {/* 
//                               👉 Submit Feedback Button Visibility Logic:
//                               Hides only when all assigned interviewers have successfully submitted feedback.
//                               If no interviewers are assigned, it will allow 1 feedback submission before hiding.
//                             */}
//                             {round.status === 'scheduled' && (
//                               (round.interviewers?.length > 0 
//                                 ? (round.feedback?.length || 0) < round.interviewers.length 
//                                 : (round.feedback?.length || 0) === 0
//                               ) && (
//                                 <button onClick={() => setSelectedInterviewForFeedback(round)} className="text-[10px] text-primary font-bold hover:underline flex items-center gap-0.5">
//                                   <Plus className="h-3.5 w-3.5" /> Submit Feedback
//                                 </button>
//                               )
//                             )}
//                           </div>
                          
//                           {round.feedback?.length > 0 ? (
//                             round.feedback.map((fb, fidx) => (
//                               <div key={fidx} className="bg-white border rounded p-2.5 text-xs space-y-1.5 shadow-xs">
//                                 <div className="flex justify-between items-center font-bold text-gray-800">
//                                   <span className="text-gray-700 text-[11px] font-semibold">
//                                     Evaluated by: {fb.interviewerId ? `${fb.interviewerId.firstName} ${fb.interviewerId.lastName}` : 'N/A'}
//                                   </span>
//                                   <span className="text-amber-500">★ {fb.rating}/5</span>
//                                 </div>
//                                 <p className="text-gray-600 italic text-[11px] leading-relaxed">"{fb.comments}"</p>
//                                 <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${fb.recommendation.includes('yes') ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
//                                   Recommendation: {fb.recommendation.replace('_', ' ')}
//                                 </span>
//                               </div>
//                             ))
//                           ) : (
//                             <p className="text-[10px] text-gray-400 italic">Feedback entries pending.</p>
//                           )}
//                         </div>
//                       </div>
//                     ))}
//                   </div>
//                 ) : (
//                   <p className="text-xs text-gray-400 italic">No interview slots scheduled yet.</p>
//                 )}
//               </div>
//             )}

//             {/* TAB 3: COMPLETE COMPREHENSIVE JOB POSTING SPECS */}
//             {activeTab === 'job_specs' && (
//               <div className="space-y-6 animate-in fade-in duration-300">
//                 <div className="flex items-center gap-2 border-b pb-3">
//                   <Info className="h-5 w-5 text-gray-400 shrink-0" />
//                   <h3 className="font-bold text-gray-900 text-sm uppercase tracking-wider">Job Specification Metrics</h3>
//                 </div>
                
//                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
//                   <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
//                     <span className="text-[9px] font-bold text-gray-400 uppercase">Division</span>
//                     <p className="text-xs font-bold text-gray-800 mt-1">{candidate.jobOpeningId?.divisionId?.name || 'N/A'}</p>
//                   </div>
//                   <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
//                     <span className="text-[9px] font-bold text-gray-400 uppercase">Department</span>
//                     <p className="text-xs font-bold text-gray-800 mt-1">{candidate.jobOpeningId?.departmentId?.name || 'N/A'}</p>
//                   </div>
//                   <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
//                     <span className="text-[9px] font-bold text-gray-400 uppercase">Hiring Mode / Location</span>
//                     <p className="text-xs font-bold text-gray-800 mt-1 capitalize">
//                       {candidate.jobOpeningId?.workMode} / {candidate.jobOpeningId?.location || 'Remote'}
//                     </p>
//                   </div>
//                   <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
//                     <span className="text-[9px] font-bold text-gray-400 uppercase">Target Openings</span>
//                     <p className="text-xs font-bold text-gray-800 mt-1">
//                       {candidate.jobOpeningId?.numberOfHires} / {candidate.jobOpeningId?.numberOfOpenings} Vacancies Filled
//                     </p>
//                   </div>
//                   <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 col-span-1 sm:col-span-2">
//                     <span className="text-[9px] font-bold text-gray-400 uppercase">Target Experience Requirements</span>
//                     <p className="text-xs font-bold text-gray-800 mt-1">
//                       {candidate.jobOpeningId?.experienceMin} to {candidate.jobOpeningId?.experienceMax || 'N/A'} Years
//                     </p>
//                   </div>
//                 </div>

//                 <div className="space-y-4 border-t pt-4">
//                   <div>
//                     <span className="text-[9px] font-bold text-gray-400 uppercase block mb-1">Job Description</span>
//                     <p className="text-xs text-gray-600 leading-relaxed bg-gray-50 p-3 rounded-lg border border-gray-100">{candidate.jobOpeningId?.description}</p>
//                   </div>
//                   <div>
//                     <span className="text-[9px] font-bold text-gray-400 uppercase block mb-1">Job Requirements</span>
//                     <ul className="list-disc pl-4 space-y-1">
//                       {candidate.jobOpeningId?.requirements?.map((req, i) => (
//                         <li key={i} className="text-xs text-gray-600 leading-relaxed">{req}</li>
//                       ))}
//                     </ul>
//                   </div>
//                   <div>
//                     <span className="text-[9px] font-bold text-gray-400 uppercase block mb-1">Job Responsibilities</span>
//                     <ul className="list-disc pl-4 space-y-1">
//                       {candidate.jobOpeningId?.responsibilities?.map((resp, i) => (
//                         <li key={i} className="text-xs text-gray-600 leading-relaxed">{resp}</li>
//                       ))}
//                     </ul>
//                   </div>
//                 </div>
//               </div>
//             )}

//           </div>
//         </div>
//       </div>


//     </div>
      
//       {/* Reusable MoveStageDrawer: Aligned with backend DEFAULT_RECRUITMENT_STAGES configuration */}
//       <MoveStageDrawer 
//         isOpen={isMoveStageOpen}
//         onClose={() => setIsMoveStageOpen(false)}
//         candidate={candidate}
//         onSave={fetchCandidateRecord} 
//       />

//       {/* Schedule/Edit Interview Offcanvas Drawer */}
//       <FormOffCanvas 
//         isOpen={isScheduleOpen} 
//         onClose={handleCloseSchedule} 
//         title={editingInterviewId ? "Edit Interview Schedule" : "Schedule Interview Slot"}
//       >
//         <form onSubmit={handleScheduleSubmit} className="space-y-4">
//           <div className="grid grid-cols-2 gap-3">
//             <div>
//               <label className="block text-xs font-bold text-gray-700 mb-1">Round No. *</label>
//               <input type="number" required min="1" value={interviewForm.round} onChange={(e) => setInterviewForm({...interviewForm, round: e.target.value})} className="w-full border rounded-lg p-2.5 text-xs outline-none" />
//             </div>
//             <div>
//               <label className="block text-xs font-bold text-gray-700 mb-1">Round Name *</label>
//               <input type="text" required placeholder="e.g. Technical Round" value={interviewForm.roundName} onChange={(e) => setInterviewForm({...interviewForm, roundName: e.target.value})} className="w-full border rounded-lg p-2.5 text-xs outline-none" />
//             </div>
//           </div>
//           <div className="grid grid-cols-2 gap-3">
//             <div>
//               <label className="block text-xs font-bold text-gray-700 mb-1">Date *</label>
//               <input 
//                 type="date" 
//                 required 
//                 value={interviewDate} 
//                 onChange={(e) => setInterviewDate(e.target.value)} 
//                 className="w-full border rounded-lg p-2.5 text-xs outline-none focus:ring-2 focus:ring-red-500" 
//               />
//             </div>
//             <div>
//               <label className="block text-xs font-bold text-gray-700 mb-1">Time *</label>
//               <input 
//                 type="time" 
//                 required 
//                 value={interviewTime} 
//                 onChange={(e) => setInterviewTime(e.target.value)} 
//                 className="w-full border rounded-lg p-2.5 text-xs outline-none focus:ring-2 focus:ring-red-500" 
//               />
//             </div>
//           </div>
//           <div>
//             <label className="block text-xs font-bold text-gray-700 mb-1">Duration (minutes)</label>
//             <input type="number" min="15" value={interviewForm.durationMinutes} onChange={(e) => setInterviewForm({...interviewForm, durationMinutes: e.target.value})} className="w-full border rounded-lg p-2.5 text-xs outline-none" />
//           </div>
          
//           {/* Meeting Mode: Updated to custom SelectDropdown */}
//           <SelectDropdown
//             label="Meeting Mode"
//             options={modeOptions}
//             value={interviewForm.mode}
//             onChange={(val) => setInterviewForm({ ...interviewForm, mode: val })}
//             valueKey="value"
//             labelKey="label"
//             searchable={false}
//             zIndex={67}
//             multiple={false}
//           />

//           {interviewForm.mode === 'online' ? (
//             <div>
//               <label className="block text-xs font-bold text-gray-700 mb-1">Google Meet / Teams Link</label>
//               <input type="url" value={interviewForm.meetingLink} onChange={(e) => setInterviewForm({...interviewForm, meetingLink: e.target.value})} className="w-full border rounded-lg p-2.5 text-xs outline-none" placeholder="https://..." />
//             </div>
//           ) : (
//             <div>
//               <label className="block text-xs font-bold text-gray-700 mb-1">Office Location</label>
//               <input type="text" value={interviewForm.location} onChange={(e) => setInterviewForm({...interviewForm, location: e.target.value})} className="w-full border rounded-lg p-2.5 text-xs outline-none" />
//             </div>
//           )}
//           {/* Dynamic Selection using SelectDropdown replacing manual text input [1] */}
//           <SelectDropdown
//             label="Select Interviewers *"
//             options={userOptions}
//             value={interviewForm.interviewers}
//             onChange={(val) => setInterviewForm({ ...interviewForm, interviewers: val })}
//             valueKey="_id"
//             labelKey="fullName"
//             searchable={true}
//             zIndex={66}
//             multiple={true} // explicitly support multi-select [1]
//           />
//           <button type="submit" disabled={isScheduling} className="w-full bg-primary hover:bg-red-700 text-white py-2.5 rounded-lg text-xs font-bold disabled:opacity-50 transition-all">
//             {isScheduling 
//               ? (editingInterviewId ? 'Rescheduling...' : 'Creating Schedule...') 
//               : (editingInterviewId ? 'Update Schedule' : 'Schedule Interview')
//             }
//           </button>
//         </form>
//       </FormOffCanvas>

//       {/* Submit Feedback Offcanvas Drawer */}
//       <FormOffCanvas isOpen={!!selectedInterviewForFeedback} onClose={() => setSelectedInterviewForFeedback(null)} title="Submit Interview Feedback">
//         {selectedInterviewForFeedback && (
//           <form onSubmit={handleFeedbackSubmit} className="space-y-4">
            
//             {/* Rating: Updated to custom SelectDropdown */}
//             <SelectDropdown
//               label="Rating (1 to 5) *"
//               options={ratingOptions}
//               value={feedbackForm.rating}
//               onChange={(val) => setFeedbackForm({ ...feedbackForm, rating: Number(val) })}
//               valueKey="value"
//               labelKey="label"
//               searchable={false}
//               zIndex={69}
//               multiple={false}
//             />

//             {/* Recommendation: Updated to custom SelectDropdown */}
//             <SelectDropdown
//               label="Recommendation *"
//               options={recommendationOptions}
//               value={feedbackForm.recommendation}
//               onChange={(val) => setFeedbackForm({ ...feedbackForm, recommendation: val })}
//               valueKey="value"
//               labelKey="label"
//               searchable={false}
//               zIndex={68}
//               multiple={false}
//             />

//             <div>
//               <label className="block text-xs font-bold text-gray-700 mb-1">Interview Comments / Technical Remarks *</label>
//               <textarea required value={feedbackForm.comments} onChange={(e) => setFeedbackForm({...feedbackForm, comments: e.target.value})} className="w-full border rounded-lg p-2.5 text-xs outline-none" rows={4} placeholder="Describe their performance, skills tested, core strengths..." />
//             </div>
//             <button type="submit" disabled={isSubmittingFeedback} className="w-full bg-primary hover:bg-red-700 text-white py-2.5 rounded-lg text-xs font-bold disabled:opacity-50 transition-all">
//               {isSubmittingFeedback ? 'Submitting Evaluation...' : 'Register Feedback'}
//             </button>
//           </form>
//         )}
//       </FormOffCanvas>
//       <CandidateForm 
//         isOpen={isEditProfileOpen}
//         onClose={() => setIsEditProfileOpen(false)}
//         mode="edit"
//         candidate={candidate}
//         onSave={fetchCandidateRecord} 
//       />
//     </>
//   );
// };

// export default CandidateDetail;

// ─────────────────────────────────────────────────────────────────────────────
// FILE: src/components/HRComponents/CandidateDetail.jsx
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { 
  Mail, Phone, MapPin, Calendar, Award, Clock, ChevronRight, 
  FileText, Plus, ShieldCheck, Star, Tags, AlertCircle, User, Info, Edit 
} from 'lucide-react';
import { toast } from 'react-toastify';
import PageLoader from '../../helpers/PageLoader';
import SummaryApi from '../../common/SummaryApi';
import api from '../../common/apiClient';
import CandidateForm from './CandidateForm';
import FormOffCanvas from '../ui/FormOffCanvas';
import MoveStageDrawer from './MoveStageDrawer';
import SelectDropdown from '../../customStyles/SelectDropdown'; // Imported custom SelectDropdown
import { formatDateTime } from '../../helpers/formatDateTime';

// Static configurations for SelectDropdown options
const modeOptions = [
  { value: 'online', label: 'Online' },
  { value: 'in_person', label: 'In Person' },
  { value: 'phone', label: 'Phone' }
];

const ratingOptions = [
  { value: 1, label: '1 - Very Poor' },
  { value: 2, label: '2 - Below Average' },
  { value: 3, label: '3 - Good' },
  { value: 4, label: '4 - Strong Candidate' },
  { value: 5, label: '5 - Excellent Fit' }
];

const recommendationOptions = [
  { value: 'strong_yes', label: 'Strong Yes' },
  { value: 'yes', label: 'Yes / Hire' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'no', label: 'No / Reject' },
  { value: 'strong_no', label: 'Strong No' }
];

const getStageBadge = (stage) => {
  const styles = {
    applied: 'bg-blue-100 text-blue-700 border-blue-200',
    resume_screening: 'bg-purple-100 text-purple-700 border-purple-200',
    shortlisted: 'bg-pink-100 text-pink-700 border-pink-200',
    interview_scheduled: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    interview_completed: 'bg-orange-100 text-orange-700 border-orange-200',
    selected: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    offered: 'bg-amber-100 text-amber-700 border-amber-200',
    onboarding: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    hired: 'bg-green-100 text-green-700 border-green-200',
    rejected: 'bg-red-100 text-red-700 border-red-200',
    on_hold: 'bg-gray-100 text-gray-700 border-gray-200',
  };
  return (
    <span className={`px-2.5 py-1 text-xs font-bold rounded-full border uppercase ${styles[stage] || 'bg-gray-50 text-gray-700'}`}>
      {stage?.replace('_', ' ')}
    </span>
  );
};

const CandidateDetail = ({ id: propId, onClose }) => { 
  const { id: urlId } = useParams();
  const id = propId || urlId; 

  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pipeline'); // 'pipeline' | 'interviews' | 'job_specs'

  // Dynamic Employee options state
  const [users, setUsers] = useState([]);

  // Stage transition Offcanvas State
  const [isMoveStageOpen, setIsMoveStageOpen] = useState(false);
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);

  // Default date and time calculations to populate current system timings [1, 2]
  const todayDateString = new Date().toISOString().split('T')[0];
  const now = new Date();
  const currentTimeString = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

  // Separate Date & Time input states [2]
  const [interviewDate, setInterviewDate] = useState(todayDateString);
  const [interviewTime, setInterviewTime] = useState(currentTimeString);

  // Schedule/Edit Interview Offcanvas State
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [editingInterviewId, setEditingInterviewId] = useState(null); // Track if we are editing an existing interview
  const [interviewForm, setInterviewForm] = useState({
    round: 1,
    roundName: '',
    durationMinutes: 30,
    mode: 'online',
    meetingLink: '',
    location: '',
    interviewers: [] // Dynamic array of selected User IDs [3]
  });
  const [isScheduling, setIsScheduling] = useState(false);

  // Submit Feedback Offcanvas State
  const [selectedInterviewForFeedback, setSelectedInterviewForFeedback] = useState(null);
  const [feedbackForm, setFeedbackForm] = useState({
    rating: 3,
    comments: '',
    recommendation: 'yes'
  });
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  // Fetch the candidate database record
  const fetchCandidateRecord = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(SummaryApi.getCandidatesById.url(id));
      if (response.data.success) {
        setCandidate(response.data.candidate);
      }
    } catch (err) {
      toast.error('Failed to resolve candidate metrics');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Fetch all employees/users from the system to populate interviewers [1]
  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get(SummaryApi.getAllUsers.url);
      if (response.data.success) {
        setUsers(response.data.data || []);
      }
    } catch (err) {
      console.error('Failed to load employees list:', err);
    }
  }, []);

  useEffect(() => {
    fetchCandidateRecord();
    fetchUsers(); // Trigger dynamic employee list load [1]
  }, [fetchCandidateRecord, fetchUsers]);

  // Merge dynamic properties of users for clear dropdown labelling
  const userOptions = useMemo(() => {
    return users.map(u => ({
      ...u,
      fullName: `${u.firstName} ${u.lastName} (${u.email})`
    }));
  }, [users]);

  // Handle Edit Click on an interview round
  const handleEditClick = (round) => {
    setEditingInterviewId(round._id);
    setInterviewForm({
      round: round.round,
      roundName: round.roundName || '',
      durationMinutes: round.durationMinutes || 30,
      mode: round.mode || 'online',
      meetingLink: round.meetingLink || '',
      location: round.location || '',
      interviewers: round.interviewers?.map((i) => i._id) || []
    });

    if (round.scheduledAt) {
      const d = new Date(round.scheduledAt);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      const timeStr = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      
      setInterviewDate(dateStr);
      setInterviewTime(timeStr);
    }
    setIsScheduleOpen(true);
  };

  // Close Schedule/Edit Modal and reset states
  const handleCloseSchedule = () => {
    setIsScheduleOpen(false);
    setEditingInterviewId(null);
    setInterviewForm({
      round: 1,
      roundName: '',
      durationMinutes: 30,
      mode: 'online',
      meetingLink: '',
      location: '',
      interviewers: []
    });
    setInterviewDate(todayDateString);
    setInterviewTime(currentTimeString);
  };

  // Combine separate date and time input values to build a single ISO string payload [2]
  const handleScheduleSubmit = async (e) => {
    e.preventDefault();
    if (!interviewDate || !interviewTime) {
      toast.warning('Please specify both Date and Time');
      return;
    }
    if (interviewForm.interviewers?.length === 0) {
      toast.warning('Please select at least one interviewer');
      return;
    }

    setIsScheduling(true);

    try {
      // Create combined ISO timestamp atomically [2]
      const scheduledAtDateTime = new Date(`${interviewDate}T${interviewTime}`).toISOString();

      const payload = {
        round: Number(interviewForm.round),
        roundName: interviewForm.roundName,
        scheduledAt: scheduledAtDateTime, // Unified Date-Time ISO payload [2]
        durationMinutes: Number(interviewForm.durationMinutes),
        mode: interviewForm.mode,
        meetingLink: interviewForm.meetingLink,
        location: interviewForm.location,
        interviewers: interviewForm.interviewers // Directly parsed array of User ObjectIds [3]
      };

      let response;
      if (editingInterviewId) {
        // Edit mode: Trigger PUT update API
        response = await api.put(
          SummaryApi.candidateScheduleInterviewUpdate.url(id, editingInterviewId), 
          payload
        );
      } else {
        // Create mode: Trigger POST schedule API
        response = await api.post(
          SummaryApi.candidateScheduleInterview.url(id), 
          payload
        );
      }

      if (response.data.success) {
        toast.success(editingInterviewId ? 'Interview rescheduled successfully' : 'Interview scheduled successfully');
        handleCloseSchedule();
        fetchCandidateRecord();
      }
    } catch (err) {
      toast.error(editingInterviewId ? 'Could not update interview schedule' : 'Could not schedule interview slot');
    } finally {
      setIsScheduling(false);
    }
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingFeedback(true);
    try {
      const response = await api.post(
        SummaryApi.candidateSubmitInterviewFeedback.url(id, selectedInterviewForFeedback._id), 
        feedbackForm
      );
      if (response.data.success) {
        toast.success('Feedback successfully registered');
        setSelectedInterviewForFeedback(null);
        setFeedbackForm({ rating: 3, comments: '', recommendation: 'yes' });
        fetchCandidateRecord();
      }
    } catch (err) {
      toast.error('Feedback record failure');
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  // Helper function to render star ratings dynamically
  const renderStars = (ratingValue) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star 
            key={star} 
            className={`h-4.5 w-4.5 ${star <= (ratingValue || 0) ? 'text-amber-500 fill-amber-500' : 'text-gray-200'}`} 
          />
        ))}
      </div>
    );
  };

  if (loading || !candidate) return <PageLoader />;

  return (
    <>
    <div className="space-y-6">
      
      {/* Hold State Alert Banner */}
      {candidate.isOnHold && (
        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-xl flex items-center gap-3 shadow-sm animate-pulse">
          <AlertCircle className="h-5 w-5 text-amber-500 shrink-0" />
          <div className="text-xs text-amber-800 font-semibold">
            This candidate is currently marked on <span className="font-extrabold uppercase">Hold</span>. Internal evaluations are paused.
          </div>
        </div>
      )}

      {/* Header Panel (Fully Responsive Flex Layout) */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 bg-white p-4 md:p-6 rounded-xl border border-gray-100 shadow-sm">
        <div className="flex items-center gap-3">
          <button 
            onClick={onClose} 
            type="button"
            className="rounded-lg bg-red-50 border border-red-100 p-2.5 text-textPrimary hover:text-primary transition-colors shrink-0"
          >
            <FaArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-lg sm:text-xl font-bold text-gray-900 leading-tight capitalize">{candidate.firstName} {candidate.lastName}</h1>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border bg-red-50 text-primary border-red-100">
                {candidate.applicationId}
              </span>
            </div>
            <p className="text-xs text-gray-400 mt-1">Applied For: <span className="font-semibold text-gray-700">{candidate.jobOpeningId?.title}</span></p>
            <p className="text-xs text-gray-400 mt-2">
             {getStageBadge(candidate.currentStage)}
            </p>
         </div>
        </div>
        
        {/* Responsive Action Buttons Grid */}
        <div className="grid grid-cols-3 gap-2 w-full lg:w-auto shrink-0">
          <button 
            onClick={() => setIsEditProfileOpen(true)} 
            className="flex items-center justify-center gap-1.5 border border-gray-300 hover:bg-gray-100 text-gray-700 px-3 py-2 rounded-lg text-xs font-bold transition-colors"
          >
            Edit Profile
          </button>
          <button 
            onClick={() => setIsScheduleOpen(true)} 
            className="flex items-center justify-center gap-1.5 border border-gray-300 hover:bg-gray-100 text-gray-700 px-3 py-2 rounded-lg text-xs font-bold"
          >
            <Calendar className="h-3.5 w-3.5" /> Schedule
          </button>
          <button 
            onClick={() => { setIsMoveStageOpen(true); }} 
            className="flex items-center justify-center gap-1.5 bg-primary hover:bg-secondary text-white px-3 py-2 rounded-lg text-xs font-bold transition-colors"
          >
            Move Stage
          </button>
        </div>
      </div>

      {/* Main Responsive Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Left Sidebar Panel (Profile Overview + Financial Metrics) */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white border rounded-xl p-5 shadow-sm space-y-5">
            <div className="flex justify-between items-center border-b pb-3">
              <h2 className="font-bold text-gray-900 text-sm uppercase tracking-wider">Candidate File</h2>
              {renderStars(candidate.rating)}
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-xs text-gray-600">
                <Mail className="h-4 w-4 text-gray-400 shrink-0" /> <span>{candidate.email}</span>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-600">
                <Phone className="h-4 w-4 text-gray-400 shrink-0" /> <span>{candidate.mobile || 'Not provided'}</span>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-600">
                <MapPin className="h-4 w-4 text-gray-400 shrink-0" /> <span>{candidate.currentLocation || 'N/A'}</span>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-600">
                <Award className="h-4 w-4 text-gray-400 shrink-0" /> <span>{candidate.totalExperienceYears} Years Total Experience</span>
              </div>
            </div>

            {/* Candidate Sourcing Details */}
            <div className="border-t pt-4 space-y-2">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Application Source</span>
              <span className="capitalize text-xs font-semibold text-gray-700 bg-gray-50 border px-3 py-1.5 rounded-lg inline-block">
                {candidate.source || 'Website'}
              </span>
            </div>

            {/* Core Skills Badges */}
            <div className="border-t pt-4 space-y-2">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Skills & Expertise</span>
              {candidate.skills?.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {candidate.skills.map((skill, idx) => (
                    <span key={idx} className="bg-red-50 text-primary border border-red-100 px-2.5 py-0.5 rounded text-[10px] font-semibold">{skill}</span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400 italic">No skills listed</p>
              )}
            </div>

            {/* Recruiter Custom Tags */}
            {candidate.tags?.length > 0 && (
              <div className="border-t pt-4 space-y-2">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                  <Tags className="h-3.5 w-3.5 text-gray-400" /> Recruiter Tags
                </span>
                <div className="flex flex-wrap gap-1">
                  {candidate.tags.map((tag, idx) => (
                    <span key={idx} className="bg-gray-100 text-gray-700 border border-gray-200 px-2 py-0.5 rounded text-[10px] font-semibold">#{tag}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Resume Upload Attachments */}
            <div className="border-t pt-4 space-y-3">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Submitted Credentials</h3>
              {candidate.documents?.length > 0 ? (
                candidate.documents.map((doc, idx) => (
                  <a key={idx} href={doc.fileUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 p-2.5 bg-gray-50 hover:bg-red-50/50 text-gray-700 rounded-lg border text-xs transition-all">
                    <FileText className="h-4.5 w-4.5 text-red-500 shrink-0" />
                    <span className="truncate flex-1 font-semibold">{doc.fileName || 'Resume Document'}</span>
                  </a>
                ))
              ) : (
                <p className="text-xs text-gray-400 italic">No credentials submitted</p>
              )}
            </div>
          </div>

          {/* Financial Profile Card with Indian Rupees Symbol formatting */}
          <div className="bg-white border rounded-xl p-5 shadow-sm space-y-4">
            <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider border-b pb-2">Financial Profile Config</h3>
            <div className="space-y-4">
              <div>
                <span className="text-[10px] font-bold text-gray-400 uppercase block">Current CTC</span>
                <p className="text-sm font-extrabold text-gray-800 mt-1 flex items-center gap-1">
                  {candidate.currentCtc ? `₹ ${candidate.currentCtc.toLocaleString('en-IN')} / year` : 'N/A'}
                </p>
              </div>
              <div>
                <span className="text-[10px] font-bold text-gray-400 uppercase block">Expected CTC</span>
                <p className="text-sm font-extrabold text-gray-800 mt-1 flex items-center gap-1">
                  {candidate.expectedCtc ? `₹ ${candidate.expectedCtc.toLocaleString('en-IN')} / year` : 'N/A'}
                </p>
              </div>
              <div>
                <span className="text-[10px] font-bold text-gray-400 uppercase block">Notice Period Duration</span>
                <p className="text-xs font-semibold text-gray-700 mt-1 bg-gray-50 border p-2.5 rounded-lg inline-block">
                  {candidate.noticePeriodDays ? `${candidate.noticePeriodDays} Days Notice` : 'Immediate Joiner'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Section Panel (Tabs Workspace with highly detailed content) */}
        <div className="lg:col-span-2 space-y-4">
          
          {/* Workspace Tabs Navigation Menu */}
          <div className="flex border-b border-gray-200 overflow-x-auto custom-scrollbar">
            <button onClick={() => setActiveTab('pipeline')} className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors whitespace-nowrap ${activeTab === 'pipeline' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              Pipeline Tracker
            </button>
            <button onClick={() => setActiveTab('interviews')} className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors whitespace-nowrap ${activeTab === 'interviews' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              Interview Cycles ({candidate.interviews?.length || 0})
            </button>
            <button onClick={() => setActiveTab('job_specs')} className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors whitespace-nowrap ${activeTab === 'job_specs' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              Job Posting Specs
            </button>
          </div>

          <div className="bg-white border rounded-xl p-6 shadow-sm min-h-[400px]">
            
            {/* TAB 1: PIPELINE / WORKFLOW LOGS TIMELINE */}
            {activeTab === 'pipeline' && (
              <div className="space-y-6 animate-in fade-in duration-300">
                <h3 className="font-bold text-gray-900 text-sm uppercase tracking-wider">Recruitment Trail Log</h3>
                {candidate.stageHistory?.length > 0 ? (
                  <div className="relative border-l border-gray-200 pl-6 ml-2 space-y-6">
                    {candidate.stageHistory.map((history, idx) => (
                      <div key={idx} className="relative">
                        <span className="absolute -left-[31px] mt-1 bg-red-100 text-primary border-4 border-white h-5 w-5 rounded-full flex items-center justify-center shadow-sm">
                          <Clock className="h-2.5 w-2.5" />
                        </span>
                        <div>
                          <div className="flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
                            <span className="capitalize font-semibold text-gray-800 bg-gray-100 px-2 py-0.5 rounded">{history.fromStage || 'Applied'}</span>
                            <ChevronRight className="h-3 w-3 text-gray-400" />
                            <span className="capitalize font-bold text-red-700 bg-red-50 px-2 py-0.5 rounded">{history.toStage}</span>
                          </div>
                          {history.remarks && <p className="text-xs text-gray-600 mt-1.5 font-medium italic">"{history.remarks}"</p>}
                          
                          {/* 👈 Fix: Safe navigation check implemented for Changer's details (Null check) */}
                          <div className="text-[10px] text-gray-400 mt-1.5 flex flex-wrap gap-x-2 gap-y-1 items-center">
                            <User className="h-3 w-3" />
                            <span className="font-semibold text-gray-500">
                              Changer: {history.changedBy?.firstName ? `${history.changedBy.firstName} ${history.changedBy.lastName} (${history.changedBy.email})` : 'System / Auto-Update'}
                            </span>
                            <span>&bull;</span>
                            <span>{formatDateTime(history.changedAt, false)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic">No transition trails logged yet.</p>
                )}
              </div>
            )}

            {/* TAB 2: INTERVIEW HISTORY (DETAILED) */}
            {activeTab === 'interviews' && (
              <div className="space-y-6 animate-in fade-in duration-300">
                <h3 className="font-bold text-gray-900 text-sm uppercase tracking-wider">Evaluation Cycles</h3>
                {candidate.interviews?.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {candidate.interviews.map((round) => (
                      <div key={round._id} className="border rounded-xl p-4 space-y-3 bg-gray-50/50 shadow-sm">
                        <div className="flex justify-between items-start border-b pb-2">
                          <div>
                            <span className="text-[9px] uppercase tracking-wider text-primary font-bold">ROUND {round.round}</span>
                            <h4 className="font-bold text-gray-800 text-xs">{round.roundName || 'Technical Round'}</h4>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {/* 
                              Edit Schedule Button:
                              Only rendered if no feedback is submitted yet.
                            */}
                            {(!round.feedback || round.feedback.length === 0) && (
                              <button 
                                onClick={() => handleEditClick(round)}
                                type="button"
                                className="p-1 hover:bg-gray-100 text-gray-500 hover:text-primary rounded transition-colors"
                                title="Edit Schedule"
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </button>
                            )}
                            <span className={`px-2 py-0.5 text-[9px] font-bold rounded capitalize border ${round.status === 'completed' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                              {round.status}
                            </span>
                          </div>
                        </div>

                        <div className="text-xs text-gray-500 space-y-1.5">
                          <div className="flex items-center gap-2">
                            <Clock className="h-3.5 w-3.5 text-gray-400 shrink-0" /> 
                            <span>{formatDateTime(round.scheduledAt, false)} ({round.durationMinutes} min)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <User className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                            <span>Scheduled By: {round.scheduledBy?.firstName ? `${round.scheduledBy.firstName} ${round.scheduledBy.lastName}` : 'System'}</span>
                          </div>
                          {round.meetingLink && (
                            <div className="flex items-center gap-2">
                              <ShieldCheck className="h-3.5 w-3.5 text-green-600 shrink-0" /> 
                              <a href={round.meetingLink} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline font-medium">Join Google Meet</a>
                            </div>
                          )}
                        </div>

                        {/* Displaying scheduled Interviewers list with complete name details (Safe navigation added) */}
                        <div className="space-y-1.5 pt-1 border-t">
                          <span className="text-[9px] font-bold text-gray-400 uppercase block">Interviewers Panel</span>
                          <div className="flex flex-wrap gap-1">
                            {round.interviewers?.map((interviewer) => (
                              <span key={interviewer?._id} className="inline-block bg-white text-gray-700 border border-gray-200 rounded px-2 py-0.5 text-[10px] font-semibold shadow-xs">
                                {interviewer?.firstName} {interviewer?.lastName} ({interviewer?.email})
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Interview Feedback Log with detailed names (Safe navigation added) */}
                        <div className="border-t pt-2 space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-[9px] font-bold text-gray-400 uppercase">Interviewer Evaluations</span>
                            
                            {/* 
                              Submit Feedback Button Visibility Logic:
                              Hides only when all assigned interviewers have successfully submitted feedback.
                              If no interviewers are assigned, it will allow 1 feedback submission before hiding.
                            */}
                            {round.status === 'scheduled' && (
                              (round.interviewers?.length > 0 
                                ? (round.feedback?.length || 0) < round.interviewers.length 
                                : (round.feedback?.length || 0) === 0
                              ) && (
                                <button onClick={() => setSelectedInterviewForFeedback(round)} className="text-[10px] text-primary font-bold hover:underline flex items-center gap-0.5">
                                  <Plus className="h-3.5 w-3.5" /> Submit Feedback
                                </button>
                              )
                            )}
                          </div>
                          
                          {round.feedback?.length > 0 ? (
                            round.feedback.map((fb, fidx) => (
                              <div key={fidx} className="bg-white border rounded p-2.5 text-xs space-y-1.5 shadow-xs">
                                <div className="flex justify-between items-center font-bold text-gray-800">
                                  <span className="text-gray-700 text-[11px] font-semibold">
                                    Evaluated by: {fb.interviewerId?.firstName ? `${fb.interviewerId.firstName} ${fb.interviewerId.lastName}` : 'N/A'}
                                  </span>
                                  <span className="text-amber-500">★ {fb.rating || 0}/5</span>
                                </div>
                                <p className="text-gray-600 italic text-[11px] leading-relaxed">"{fb.comments}"</p>
                                <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${fb.recommendation?.includes('yes') ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                                  Recommendation: {fb.recommendation?.replace('_', ' ') || 'N/A'}
                                </span>
                              </div>
                            ))
                          ) : (
                            <p className="text-[10px] text-gray-400 italic">Feedback entries pending.</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic">No interview slots scheduled yet.</p>
                )}
              </div>
            )}

            {/* TAB 3: COMPLETE COMPREHENSIVE JOB POSTING SPECS */}
            {activeTab === 'job_specs' && (
              <div className="space-y-6 animate-in fade-in duration-300">
                <div className="flex items-center gap-2 border-b pb-3">
                  <Info className="h-5 w-5 text-gray-400 shrink-0" />
                  <h3 className="font-bold text-gray-900 text-sm uppercase tracking-wider">Job Specification Metrics</h3>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                    <span className="text-[9px] font-bold text-gray-400 uppercase">Division</span>
                    <p className="text-xs font-bold text-gray-800 mt-1">{candidate.jobOpeningId?.divisionId?.name || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                    <span className="text-[9px] font-bold text-gray-400 uppercase">Department</span>
                    <p className="text-xs font-bold text-gray-800 mt-1">{candidate.jobOpeningId?.departmentId?.name || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                    <span className="text-[9px] font-bold text-gray-400 uppercase">Hiring Mode / Location</span>
                    <p className="text-xs font-bold text-gray-800 mt-1 capitalize">
                      {candidate.jobOpeningId?.workMode} / {candidate.jobOpeningId?.location || 'Remote'}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                    <span className="text-[9px] font-bold text-gray-400 uppercase">Target Openings</span>
                    <p className="text-xs font-bold text-gray-800 mt-1">
                      {candidate.jobOpeningId?.numberOfHires} / {candidate.jobOpeningId?.numberOfOpenings} Vacancies Filled
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 col-span-1 sm:col-span-2">
                    <span className="text-[9px] font-bold text-gray-400 uppercase">Target Experience Requirements</span>
                    <p className="text-xs font-bold text-gray-800 mt-1">
                      {candidate.jobOpeningId?.experienceMin} to {candidate.jobOpeningId?.experienceMax || 'N/A'} Years
                    </p>
                  </div>
                </div>

                <div className="space-y-4 border-t pt-4">
                  <div>
                    <span className="text-[9px] font-bold text-gray-400 uppercase block mb-1">Job Description</span>
                    <p className="text-xs text-gray-600 leading-relaxed bg-gray-50 p-3 rounded-lg border border-gray-100">{candidate.jobOpeningId?.description}</p>
                  </div>
                  <div>
                    <span className="text-[9px] font-bold text-gray-400 uppercase block mb-1">Job Requirements</span>
                    <ul className="list-disc pl-4 space-y-1">
                      {candidate.jobOpeningId?.requirements?.map((req, i) => (
                        <li key={i} className="text-xs text-gray-600 leading-relaxed">{req}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <span className="text-[9px] font-bold text-gray-400 uppercase block mb-1">Job Responsibilities</span>
                    <ul className="list-disc pl-4 space-y-1">
                      {candidate.jobOpeningId?.responsibilities?.map((resp, i) => (
                        <li key={i} className="text-xs text-gray-600 leading-relaxed">{resp}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>


    </div>
      
      {/* Reusable MoveStageDrawer: Aligned with backend DEFAULT_RECRUITMENT_STAGES configuration */}
      <MoveStageDrawer 
        isOpen={isMoveStageOpen}
        onClose={() => setIsMoveStageOpen(false)}
        candidate={candidate}
        onSave={fetchCandidateRecord} 
      />

      {/* Schedule/Edit Interview Offcanvas Drawer */}
      <FormOffCanvas 
        isOpen={isScheduleOpen} 
        onClose={handleCloseSchedule} 
        title={editingInterviewId ? "Edit Interview Schedule" : "Schedule Interview Slot"}
      >
        <form onSubmit={handleScheduleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Round No. *</label>
              <input type="number" required min="1" value={interviewForm.round} onChange={(e) => setInterviewForm({...interviewForm, round: e.target.value})} className="w-full border rounded-lg p-2.5 text-xs outline-none" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Round Name *</label>
              <input type="text" required placeholder="e.g. Technical Round" value={interviewForm.roundName} onChange={(e) => setInterviewForm({...interviewForm, roundName: e.target.value})} className="w-full border rounded-lg p-2.5 text-xs outline-none" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Date *</label>
              <input 
                type="date" 
                required 
                value={interviewDate} 
                onChange={(e) => setInterviewDate(e.target.value)} 
                className="w-full border rounded-lg p-2.5 text-xs outline-none focus:ring-2 focus:ring-red-500" 
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Time *</label>
              <input 
                type="time" 
                required 
                value={interviewTime} 
                onChange={(e) => setInterviewTime(e.target.value)} 
                className="w-full border rounded-lg p-2.5 text-xs outline-none focus:ring-2 focus:ring-red-500" 
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Duration (minutes)</label>
            <input type="number" min="15" value={interviewForm.durationMinutes} onChange={(e) => setInterviewForm({...interviewForm, durationMinutes: e.target.value})} className="w-full border rounded-lg p-2.5 text-xs outline-none" />
          </div>
          
          {/* Meeting Mode: Updated to custom SelectDropdown */}
          <SelectDropdown
            label="Meeting Mode"
            options={modeOptions}
            value={interviewForm.mode}
            onChange={(val) => setInterviewForm({ ...interviewForm, mode: val })}
            valueKey="value"
            labelKey="label"
            searchable={false}
            zIndex={67}
            multiple={false}
          />

          {interviewForm.mode === 'online' ? (
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Google Meet / Teams Link</label>
              <input type="url" value={interviewForm.meetingLink} onChange={(e) => setInterviewForm({...interviewForm, meetingLink: e.target.value})} className="w-full border rounded-lg p-2.5 text-xs outline-none" placeholder="https://..." />
            </div>
          ) : (
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Office Location</label>
              <input type="text" value={interviewForm.location} onChange={(e) => setInterviewForm({...interviewForm, location: e.target.value})} className="w-full border rounded-lg p-2.5 text-xs outline-none" />
            </div>
          )}
          {/* Dynamic Selection using SelectDropdown replacing manual text input [1] */}
          <SelectDropdown
            label="Select Interviewers *"
            options={userOptions}
            value={interviewForm.interviewers}
            onChange={(val) => setInterviewForm({ ...interviewForm, interviewers: val })}
            valueKey="_id"
            labelKey="fullName"
            searchable={true}
            zIndex={66}
            multiple={true} // explicitly support multi-select [1]
          />
          <button type="submit" disabled={isScheduling} className="w-full bg-primary hover:bg-red-700 text-white py-2.5 rounded-lg text-xs font-bold disabled:opacity-50 transition-all">
            {isScheduling 
              ? (editingInterviewId ? 'Rescheduling...' : 'Creating Schedule...') 
              : (editingInterviewId ? 'Update Schedule' : 'Schedule Interview')
            }
          </button>
        </form>
      </FormOffCanvas>

      {/* Submit Feedback Offcanvas Drawer */}
      <FormOffCanvas isOpen={!!selectedInterviewForFeedback} onClose={() => setSelectedInterviewForFeedback(null)} title="Submit Interview Feedback">
        {selectedInterviewForFeedback && (
          <form onSubmit={handleFeedbackSubmit} className="space-y-4">
            
            {/* Rating: Updated to custom SelectDropdown */}
            <SelectDropdown
              label="Rating (1 to 5) *"
              options={ratingOptions}
              value={feedbackForm.rating}
              onChange={(val) => setFeedbackForm({ ...feedbackForm, rating: Number(val) })}
              valueKey="value"
              labelKey="label"
              searchable={false}
              zIndex={69}
              multiple={false}
            />

            {/* Recommendation: Updated to custom SelectDropdown */}
            <SelectDropdown
              label="Recommendation *"
              options={recommendationOptions}
              value={feedbackForm.recommendation}
              onChange={(val) => setFeedbackForm({ ...feedbackForm, recommendation: val })}
              valueKey="value"
              labelKey="label"
              searchable={false}
              zIndex={68}
              multiple={false}
            />

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Interview Comments / Technical Remarks *</label>
              <textarea required value={feedbackForm.comments} onChange={(e) => setFeedbackForm({...feedbackForm, comments: e.target.value})} className="w-full border rounded-lg p-2.5 text-xs outline-none" rows={4} placeholder="Describe their performance, skills tested, core strengths..." />
            </div>
            <button type="submit" disabled={isSubmittingFeedback} className="w-full bg-primary hover:bg-red-700 text-white py-2.5 rounded-lg text-xs font-bold disabled:opacity-50 transition-all">
              {isSubmittingFeedback ? 'Submitting Evaluation...' : 'Register Feedback'}
            </button>
          </form>
        )}
      </FormOffCanvas>
      <CandidateForm 
        isOpen={isEditProfileOpen}
        onClose={() => setIsEditProfileOpen(false)}
        mode="edit"
        candidate={candidate}
        onSave={fetchCandidateRecord} 
      />
    </>
  );
};

export default CandidateDetail;